/**
 * Strict supported-assets whitelist loader.
 *
 * Single source of truth: `packages/shared/src/data/supportedAssets.json`. The
 * JSON is imported as an ES module (resolveJsonModule) so the loader works in
 * Node, tsx, and Vite — no `node:fs` dependency, runs in the browser bundle.
 * On any structural defect (missing fields, duplicate ids, unknown chain refs,
 * native-with-contract, non-native-with-null contract, etc.) this module
 * THROWS at import time so seed and the API refuse to boot with a malformed
 * whitelist.
 *
 * Used by the seed (apps/api/prisma/seed.ts), the strict sync ingest validator
 * (apps/api/src/sync/syncWallet.ts), the migration generator script, and the
 * frontend (apps/web).
 */

import { z } from "zod";
import rawWhitelist from "./data/supportedAssets.json" with { type: "json" };

export type SupportedChainId =
  | "BTC"
  | "ETH"
  | "TRX"
  | "BSC"
  | "POLYGON"
  | "ARB"
  | "OP"
  | "BASE"
  | "AVAX"
  | "SOL"
  | "XRP";

export type AddressFormat = "btc" | "evm" | "tron" | "solana" | "xrpl";
export type AssetStandard = "native" | "ERC20" | "TRC20" | "BEP20" | "SPL";
export type AssetCategory =
  | "native"
  | "wrapped-native"
  | "stablecoin"
  | "wrapped-btc";

export interface SupportedChain {
  id: SupportedChainId;
  name: string;
  addressFormat: AddressFormat;
  active: boolean;
  explorerTxUrlTemplate: string;
  explorerAddressUrlTemplate: string;
}

export interface SupportedAsset {
  id: string;
  chainId: SupportedChainId;
  symbol: string;
  standard: AssetStandard;
  contractAddress: string | null;
  decimals: number;
  category: AssetCategory;
  displayName: string;
}

const SUPPORTED_CHAIN_IDS = [
  "BTC",
  "ETH",
  "TRX",
  "BSC",
  "POLYGON",
  "ARB",
  "OP",
  "BASE",
  "AVAX",
  "SOL",
  "XRP",
] as const;

const ADDRESS_FORMATS = ["btc", "evm", "tron", "solana", "xrpl"] as const;
const ASSET_STANDARDS = ["native", "ERC20", "TRC20", "BEP20", "SPL"] as const;
const ASSET_CATEGORIES = [
  "native",
  "wrapped-native",
  "stablecoin",
  "wrapped-btc",
] as const;

const ChainSchema = z
  .object({
    id: z.enum(SUPPORTED_CHAIN_IDS),
    name: z.string().min(1),
    addressFormat: z.enum(ADDRESS_FORMATS),
    active: z.boolean(),
    explorerTxUrlTemplate: z.string().min(1),
    explorerAddressUrlTemplate: z.string().min(1),
  })
  .strict();

const AssetSchema = z
  .object({
    id: z.string().min(1),
    chainId: z.enum(SUPPORTED_CHAIN_IDS),
    symbol: z.string().min(1),
    standard: z.enum(ASSET_STANDARDS),
    contractAddress: z.string().min(1).nullable(),
    decimals: z.number().int().min(0).max(36),
    category: z.enum(ASSET_CATEGORIES),
    displayName: z.string().min(1),
  })
  .strict();

/**
 * Internal — exported for unit tests so the structural rules can be exercised
 * against synthetic fixtures without writing them to disk. NOT re-exported
 * from the package barrel; consumers must use the loader API.
 */
export const supportedAssetsSchema = z
  .object({
    version: z.literal(1),
    chains: z.array(ChainSchema).min(1),
    assets: z.array(AssetSchema).min(1),
  })
  .strict()
  .superRefine((data, ctx) => {
    const chainById = new Map<string, z.infer<typeof ChainSchema>>();
    for (const chain of data.chains) {
      if (chainById.has(chain.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["chains"],
          message: `Duplicate chain id "${chain.id}".`,
        });
        continue;
      }
      chainById.set(chain.id, chain);
    }

    const assetIds = new Set<string>();
    const contractKeys = new Set<string>();
    for (const asset of data.assets) {
      if (assetIds.has(asset.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assets"],
          message: `Duplicate asset id "${asset.id}".`,
        });
      }
      assetIds.add(asset.id);

      const chain = chainById.get(asset.chainId);
      if (!chain) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assets"],
          message: `Asset "${asset.id}" references unknown chainId "${asset.chainId}".`,
        });
        continue;
      }

      const isNative = asset.standard === "native";
      if (isNative && asset.contractAddress !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assets"],
          message: `Native asset "${asset.id}" must have contractAddress=null.`,
        });
      }
      if (!isNative && asset.contractAddress === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assets"],
          message: `Non-native asset "${asset.id}" must have a contractAddress.`,
        });
      }

      // BTC / XRP only carry the native asset in this whitelist (BTC has no
      // contracts at all; XRPL issued currencies are intentionally out of
      // scope per the spec — they need an issuer field, not modeled here).
      if (chain.id === "BTC" && !isNative) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assets"],
          message: `BTC chain admits only the native asset; "${asset.id}" violates this.`,
        });
      }
      if (chain.id === "XRP" && !isNative) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assets"],
          message: `XRP chain admits only the native asset in this whitelist; "${asset.id}" violates this.`,
        });
      }

      if (asset.contractAddress !== null) {
        // Normalize per chain to detect duplicates the same way `lookupAsset`
        // does. EVM lowercases; TRON / Solana stay case-sensitive.
        const normalized =
          chain.addressFormat === "evm"
            ? asset.contractAddress.toLowerCase()
            : asset.contractAddress;
        const key = `${asset.chainId}:${normalized}`;
        if (contractKeys.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["assets"],
            message: `Duplicate (chainId, contractAddress) on "${asset.chainId}": ${normalized}`,
          });
        }
        contractKeys.add(key);
      }
    }
  });

/**
 * Lower-case EVM contracts, leave TRON / Solana untouched (both are
 * base58-encoded and case-sensitive — `t...` and `T...` are distinct addresses
 * on TRON, which is exactly what stops a spam token using a lower-cased name
 * from colliding with the canonical USDT contract).
 */
export function normalizeContract(
  chainId: SupportedChainId,
  addr: string,
): string {
  switch (chainId) {
    case "ETH":
    case "BSC":
    case "POLYGON":
    case "ARB":
    case "OP":
    case "BASE":
    case "AVAX":
      return addr.toLowerCase();
    case "TRX":
    case "SOL":
      return addr;
    case "BTC":
      throw new Error("BTC has no contracts");
    case "XRP":
      throw new Error("XRP has no contracts in this whitelist");
  }
}

const NATIVE_KEY = "__native__";

function loadAndValidate(): {
  chains: readonly SupportedChain[];
  assets: readonly SupportedAsset[];
  byChainContract: Map<string, Map<string, SupportedAsset>>;
} {
  const data = supportedAssetsSchema.parse(rawWhitelist);

  const chains: SupportedChain[] = data.chains.map((c) => ({ ...c }));
  const assets: SupportedAsset[] = data.assets.map((a) => ({ ...a }));

  const byChainContract = new Map<string, Map<string, SupportedAsset>>();
  for (const chain of chains) {
    byChainContract.set(chain.id, new Map());
  }
  for (const asset of assets) {
    const chainMap = byChainContract.get(asset.chainId);
    if (!chainMap) {
      // Should be unreachable: the schema's superRefine catches unknown
      // chainId references before we get here.
      throw new Error(
        `Internal: asset "${asset.id}" references unknown chain "${asset.chainId}".`,
      );
    }
    if (asset.contractAddress === null) {
      chainMap.set(NATIVE_KEY, asset);
    } else {
      chainMap.set(normalizeContract(asset.chainId, asset.contractAddress), asset);
    }
  }

  return {
    chains: Object.freeze(chains.map((c) => Object.freeze(c))),
    assets: Object.freeze(assets.map((a) => Object.freeze(a))),
    byChainContract,
  };
}

const STATE = loadAndValidate();

const WHITELIST_VERSION = (rawWhitelist as { version: number }).version;

export function getWhitelistVersion(): number {
  return WHITELIST_VERSION;
}

export function getSupportedChains(): readonly SupportedChain[] {
  return STATE.chains;
}

export function getSupportedAssets(): readonly SupportedAsset[] {
  return STATE.assets;
}

export function isSupportedChain(
  chainId: string,
): chainId is SupportedChainId {
  return (SUPPORTED_CHAIN_IDS as readonly string[]).includes(chainId);
}

export function getSupportedChain(chainId: string): SupportedChain | null {
  if (!isSupportedChain(chainId)) return null;
  return STATE.chains.find((c) => c.id === chainId) ?? null;
}

/**
 * §7 "if any doubt, REJECT": every miss returns null. Throwing is reserved
 * for `normalizeContract` on chains without a contract space (BTC / XRP),
 * and even then this function silently swallows that into a null so a runtime
 * caller (sync) gets a uniform "drop the tx" signal.
 */
export function lookupAsset(
  chainId: string,
  contractAddress: string | null,
): SupportedAsset | null {
  if (!isSupportedChain(chainId)) return null;
  const chainMap = STATE.byChainContract.get(chainId);
  if (!chainMap) return null;
  if (contractAddress === null) {
    return chainMap.get(NATIVE_KEY) ?? null;
  }
  let normalized: string;
  try {
    normalized = normalizeContract(chainId, contractAddress);
  } catch {
    // BTC / XRP have no contracts — any non-null contract on those chains
    // is a soft reject, never a throw.
    return null;
  }
  return chainMap.get(normalized) ?? null;
}
